import { Form, Input, Modal, Select, Tag, message } from "antd";
import { useEffect, useMemo, useState } from "react";
import {
  InvestmentMatchReviewFamily,
  InvestmentMatchReviewGroup,
  InvestmentMatchReviewTarget,
  InvestmentMatchReviewTargetKind,
} from "../../entities/investmentReview";
import {
  useCreateInvestmentReviewOtherCompanyMutation,
  useIgnoreInvestmentReviewGroupMutation,
  useLazySearchInvestmentReviewTargetsQuery,
  useResolveInvestmentReviewGroupMutation,
} from "../../features/api/investmentReviewApi";

type Mode = "resolve" | "create" | "ignore";

interface Props {
  mode?: Mode;
  group?: InvestmentMatchReviewGroup;
  onClose: () => void;
}

const getErrorText = (error: any) =>
  error?.data?.error ?? error?.data?.globalErrors?.[0] ?? error?.error ?? "عملیات با خطا مواجه شد";

const targetKindLabel = (kind: InvestmentMatchReviewTargetKind) => {
  switch (kind) {
    case InvestmentMatchReviewTargetKind.Company:
      return "Company";
    case InvestmentMatchReviewTargetKind.OtherCompany:
      return "OtherCompany";
    case InvestmentMatchReviewTargetKind.Industry:
      return "Industry";
  }
};

export const InvestmentReviewDecisionModal = ({ mode, group, onClose }: Props) => {
  const [form] = Form.useForm();
  const [targetSearch, setTargetSearch] = useState("");
  const [industrySearch, setIndustrySearch] = useState("");
  const [searchTargets, targetsState] = useLazySearchInvestmentReviewTargetsQuery();
  const [searchIndustries, industriesState] = useLazySearchInvestmentReviewTargetsQuery();
  const [resolve, resolveState] = useResolveInvestmentReviewGroupMutation();
  const [createOtherCompany, createState] = useCreateInvestmentReviewOtherCompanyMutation();
  const [ignore, ignoreState] = useIgnoreInvestmentReviewGroupMutation();

  const open = !!mode && !!group;
  const sampleTitle = group?.sampleSourceTitles?.[0] ?? group?.normalizedTitle ?? "";
  const defaultTargetSearch = (group?.normalizedTitle ?? sampleTitle).replace(/\s+حق\s+تقدم$/, "").trim();

  useEffect(() => {
    if (!open || !group) return;
    form.resetFields();
    form.setFieldsValue({ canonicalName: sampleTitle });
    setTargetSearch(defaultTargetSearch || sampleTitle);
    setIndustrySearch("");

    if (mode === "resolve") {
      searchTargets({ family: group.family, search: defaultTargetSearch || sampleTitle, maxResults: 100 });
    } else if (mode === "create") {
      searchIndustries({ family: InvestmentMatchReviewFamily.Industry, search: "", maxResults: 500 });
    }
  }, [open, group, mode, form, sampleTitle, defaultTargetSearch, searchTargets]);

  useEffect(() => {
    if (!open || mode !== "resolve" || !group || targetSearch.trim().length < 2) return;
    const handle = window.setTimeout(() => {
      searchTargets({ family: group.family, search: targetSearch.trim(), maxResults: 100 });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [targetSearch, open, mode, group, searchTargets]);

  useEffect(() => {
    if (!open || mode !== "create" || industrySearch.trim().length < 2) return;
    const handle = window.setTimeout(() => {
      searchIndustries({ family: InvestmentMatchReviewFamily.Industry, search: industrySearch.trim(), maxResults: 500 });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [industrySearch, open, mode, searchIndustries]);

  const targetOptions = useMemo(
    () =>
      (targetsState.data?.targets ?? []).map((target: InvestmentMatchReviewTarget) => ({
        value: `${target.kind}:${target.id}`,
        label: (
          <span>
            {target.name}{target.secondaryName ? ` (${target.secondaryName})` : ""} <Tag className="mr-1">{targetKindLabel(target.kind)}</Tag>
            {target.isExactNormalizedMatch && <Tag color="green">Exact</Tag>}
            {!target.isExactNormalizedMatch && target.isCompactMatch && <Tag color="blue">Compact</Tag>}
          </span>
        ),
      })),
    [targetsState.data],
  );

  const industryOptions = useMemo(
    () =>
      (industriesState.data?.targets ?? [])
        .filter((target) => target.kind === InvestmentMatchReviewTargetKind.Industry)
        .map((target) => ({ value: target.id, label: target.name })),
    [industriesState.data],
  );

  const loading = resolveState.isLoading || createState.isLoading || ignoreState.isLoading;

  const submit = async () => {
    if (!group || !mode) return;
    try {
      const values = await form.validateFields();

      if (mode === "resolve") {
        const [kind, id] = String(values.target).split(":").map(Number);
        const result = await resolve({
          family: group.family,
          normalizedTitle: group.normalizedTitle,
          targetKind: kind,
          targetId: id,
          expectedOccurrenceCount: group.occurrenceCount,
        }).unwrap();
        message.success(`${result.updatedOccurrenceCount} مورد به «${result.targetName}» متصل شد${result.aliasCreated ? " و alias ثبت شد" : ""}.`);
      } else if (mode === "create") {
        const result = await createOtherCompany({
          family: group.family,
          normalizedTitle: group.normalizedTitle,
          canonicalName: values.canonicalName,
          industryId: values.industryId,
          expectedOccurrenceCount: group.occurrenceCount,
        }).unwrap();
        message.success(`OtherCompany «${result.otherCompanyName}» ساخته شد و ${result.updatedOccurrenceCount} مورد اصلاح شد.`);
      } else {
        const result = await ignore({
          family: group.family,
          normalizedTitle: group.normalizedTitle,
          expectedOccurrenceCount: group.occurrenceCount,
          reason: values.reason,
        }).unwrap();
        message.success(`${result.updatedOccurrenceCount} مورد Ignore شد و rule پایدار ثبت شد.`);
      }

      onClose();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(getErrorText(error));
    }
  };

  const title = mode === "resolve" ? "اتصال به هویت موجود" : mode === "create" ? "ساخت OtherCompany" : "Ignore پایدار";

  return (
    <Modal open={open} title={title} onCancel={onClose} onOk={submit} confirmLoading={loading} okText="تایید" cancelText="لغو" destroyOnClose>
      {group && (
        <div className="mb-4 rounded bg-slate-50 p-3 text-sm">
          <div className="font-semibold">{sampleTitle}</div>
          <div className="mt-1 text-xs text-slate-500 break-all">{group.normalizedTitle}</div>
          <div className="mt-1">{group.occurrenceCount} occurrence در {group.reportCount} گزارش</div>
        </div>
      )}

      <Form form={form} layout="vertical">
        {mode === "resolve" && (
          <Form.Item name="target" label="هویت مقصد" rules={[{ required: true, message: "هویت مقصد را انتخاب کنید" }]}>
            <Select
              showSearch
              filterOption={false}
              options={targetOptions}
              loading={targetsState.isFetching}
              onSearch={setTargetSearch}
              placeholder="نام شرکت یا صنعت را جستجو کنید"
              notFoundContent={targetSearch.trim().length < 2 ? "حداقل دو حرف وارد کنید" : undefined}
            />
          </Form.Item>
        )}

        {mode === "create" && (
          <>
            <Form.Item
              name="canonicalName"
              label="نام OtherCompany"
              extra="نام باید به همان normalized key گروه برسد؛ backend برخورد هویتی را دوباره کنترل می‌کند."
              rules={[{ required: true, message: "نام را وارد کنید" }]}>
              <Input maxLength={1024} />
            </Form.Item>
            <Form.Item name="industryId" label="صنعت (اختیاری)">
              <Select
                allowClear
                showSearch
                filterOption={false}
                options={industryOptions}
                loading={industriesState.isFetching}
                onSearch={setIndustrySearch}
                placeholder="در صورت نیاز صنعت را جستجو کنید"
              />
            </Form.Item>
          </>
        )}

        {mode === "ignore" && (
          <Form.Item name="reason" label="دلیل (اختیاری)">
            <Input.TextArea rows={4} maxLength={2048} showCount />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};
